import superagentRequest from 'superagent'
import superagentRetryDelay from 'superagent-retry-delay'
import { map, get } from 'lodash-es'
import Cookies from 'cookies-js'

import isOnePlatformEnabled from '../../isOnePlatformEnabled'
import TokenService from '../../authService/TokenService'

import { getAbsoluteUrl, getApiBaseUrl } from './getUrl'
import { sentryReportError } from './sentryConfig'

superagentRequest.parse['application/x-javascript'] = JSON.parse

superagentRetryDelay(superagentRequest)

const requestData = (options) => {
  let url = get(options, 'url', '/api/echo')

  url = isOnePlatformEnabled ? url : getAbsoluteUrl(url, getApiBaseUrl())

  return {
    method: 'POST',
    success: () => {},
    error: () => {},
    ...options,
    url, // it comes after options so it can't be overwritten
    data: {
      ...options.data,
      format: 'json',
      raw: '1',
    },
  }
}

/**
 * Higher-order function to control the behavior of superagentRequest.
 *
 * @param {() => superagentRequest} requestFunction Function that returns the SuperAgentRequest to apply options to
 * @param {{}} defaultOptions Default options to apply to the result of function
 * @returns {() => superagentRequest} Request with default or provided options
 */

const withOptions = (requestFunction, defaultOptions) => {
  return (data) => {
    let newRequest = requestFunction(data)

    const { isSendingCredentials = true, retryOptions = [0, [], []] } = {
      ...defaultOptions,
      ...data,
    }

    if (isSendingCredentials) {
      newRequest = newRequest.withCredentials()
    }

    // Because of how the tests are written, the retry call should always be returned.
    return newRequest.retry(...retryOptions)
  }
}

const requestPromiseV1 = withOptions((data) => {
  let request = superagentRequest

  if (data.method === 'POST') {
    request = request.post(data.url).type('form').send(data.data)
  } else {
    request = request.get(data.url).query(data.data)
  }

  return request
})

const requestUploadPromiseV1 = withOptions((data) => {
  const request = superagentRequest.post(data.url).field(data.data)

  if (data.onProgress) {
    request.on('progress', data.onProgress)
  }

  map(data.files, (file) => request.attach(...file))

  return request
})

const prefix = (prefixString, url) => {
  const prefixStringWithoutLastTrailingSlash = prefixString.replace(/\/$/, '')

  return prefixStringWithoutLastTrailingSlash + url
}

const requestPromiseV2 = withOptions((data) => {
  const site = TokenService.getInstance().getCurrentSiteToken()

  const { bearer_token: bearerToken, api_base_url: apiBaseUrl } = site

  const cookie = Cookies.get('_tt_dev_env')

  if (data.method === 'POST') {
    const req = superagentRequest
      .post(prefix(apiBaseUrl, data.url))
      .type('form')
      .set('Authorization', `Bearer ${bearerToken}`)

    if (cookie) {
      req.set('TwentyThree-Development-Environment', cookie)
    }

    return req.send(data.data)
  }

  const req = superagentRequest
    .get(prefix(apiBaseUrl, data.url))
    .set('Authorization', `Bearer ${bearerToken}`)

  if (cookie) {
    req.set('TwentyThree-Development-Environment', cookie)
  }
  return req.query(data.data)
})

const requestUploadPromiseV2 = withOptions((data) => {
  const site = TokenService.getInstance().getCurrentSiteToken()

  const { bearer_token: bearerToken, api_base_url: apiBaseUrl } = site

  const request = superagentRequest
    .post(prefix(apiBaseUrl, data.url))
    .set('Authorization', `Bearer ${bearerToken}`)
    .field(data.data)

  const cookie = Cookies.get('_tt_dev_env')

  if (cookie) {
    request.set('TwentyThree-Development-Environment', cookie)
  }

  if (data.onProgress) {
    request.on('progress', data.onProgress)
  }

  map(data.files, (file) => request.attach(...file))

  return request
})

const parseResponse = (response) => {
  const json = JSON.parse(response.text)

  if (json.status === 'error') {
    const err = new Error(json.message || 'Undefined error')
    err.name = json.code

    throw err
  }

  return json
}

/**
 * Wraps the request in a try catch and will replay the request once to get a new token if the first request fails.
 */
const handleRequest = async (opts, requestFunc) => {
  const data = requestData(opts)
  try {
    // TODO(emrah): Fix me properly -- refactor use cases of data.success()
    const json = parseResponse(await requestFunc(data))
    data.success(json)
    return json
  } catch (e) {
    // Retry the request if One platform is enabled
    if (isOnePlatformEnabled && e.response?.status === 401) {
      try {
        TokenService.getInstance().clearTokens()
        await TokenService.getInstance().fetchTokens()
        // TODO(emrah): Fix me properly -- refactor use cases of data.success()
        const json = parseResponse(await requestFunc(data))
        data.success(json)
        return json
      } catch (error) {
        // Escape hatch
        window.location.reload()
      }
    }
    sentryReportError(e)

    // TODO(emrah): Fix me properly -- refactor use cases of data.error()
    data.error(e)
    throw e
  }
}

const requestPromise = isOnePlatformEnabled
  ? requestPromiseV2
  : requestPromiseV1

const requestUploadPromise = isOnePlatformEnabled
  ? requestUploadPromiseV2
  : requestUploadPromiseV1

const apiRequest = (opts) => handleRequest(opts, requestPromise)
const apiUpload = (opts) => handleRequest(opts, requestUploadPromise)

/**
 * This is version 2
 * In order to support one platform, we need
 * to call the api with a different api base url and send bearer tokens
 * for authorization.
 */

function apiMiddleware({ dispatch }) {
  return (next) => (action) => {
    const {
      types,
      request,
      success: successCallback,
      error: errorCallback,
    } = action

    if (typeof types !== 'object' || typeof request !== 'object') {
      return next(action)
    }

    if (types.length !== 3) {
      throw new Error('Expected "types" to be array of 3')
    }

    const [INIT, SUCCESS, ERROR] = types

    dispatch({ type: INIT, requestData: request.data })

    request.success = (data) => {
      dispatch({
        type: SUCCESS,
        data,
        requestData: request.data,
      })
      if (typeof successCallback !== 'undefined') {
        successCallback(data, dispatch)
      }
    }

    request.error = (error, data) => {
      dispatch({
        type: ERROR,
        error,
        data,
        requestData: request.data,
      })
      if (typeof errorCallback !== 'undefined') {
        errorCallback(error, data, dispatch)
      }
    }

    return apiRequest(request)
  }
}

export { apiRequest as request, apiUpload as requestUpload, apiMiddleware }
